/**
 * ----------------------------------------------------------------------------------------------------------------------
 * PRODUCT GALLERY COMPONENT - LOAD MORE FUNCTIONALITY
 * ----------------------------------------------------------------------------------------------------------------------
 * 
 * This script handles the "Load More" functionality for the product gallery component.
 * 
 * The product gallery component is used to show the product gallery in:
 * - Product page
 * - Featured product section
 * 
 * This specific script manages the behavior of the "Load More" button when:
 * - desktop_layout is set to "grid_2x"
 * - There are 7 or more visible images
 * 
 * It handles:
 * - Showing/hiding the "Load More" button based on visible image count
 * - Toggling visibility of images beyond the first 6
 * - Responding to variant changes that may affect image visibility
 * - Ensuring consistent behavior across page loads and cache states
 * - Excluding images with alt="card-only" from all calculations and display
 *
 * PRODUCT GALLERY COMPONENT - LOAD MORE FUNCTIONALITY
 * - Resets to collapsed state when variant changes
 * - No localStorage dependency
 * - Filters out card-only images
 */

document.addEventListener("DOMContentLoaded", () => {
  // Function to handle the load more functionality
  function setupProductGallery() {
    const carousel = document.querySelector(".product .product-gallery__carousel");
    if (!carousel) return;
    
    const viewMoreBtn = document.querySelector(".view-more-btn");
    if (!viewMoreBtn) return;
    
    // Only run this code if we're in grid_2x layout
    const galleryElement = document.querySelector(".product .product-gallery");
    if (!galleryElement || !galleryElement.classList.contains("two-column-gallery")) {
      // Hide the button if we're not in grid_2x layout
      viewMoreBtn.style.display = "none";
      return;
    }

    const updateVisibleMediaItems = () => {
      // Get all media items that are not hidden AND don't have card-only images
      const allMediaItems = Array.from(carousel.querySelectorAll(".product-gallery__media:not([hidden])"));
      
      // Filter out items that contain images with alt="card-only"
      const visibleDivs = allMediaItems.filter(div => {
        const img = div.querySelector('img');
        if (img && img.getAttribute('alt') === 'card-only') {
          // Hide the entire media div if it contains a card-only image
          div.style.display = 'none';
          return false;
        }
        // Show the div if it doesn't contain a card-only image
        div.style.display = '';
        return true;
      });

      const contents = document.querySelector('.product .product-gallery .product-gallery--carousel');
      if (!contents) return;

      // Only show the button if we have 7 or more visible items (excluding card-only)
      if (visibleDivs.length >= 7) {
        viewMoreBtn.style.display = "block";
        contents.classList.add("more-contents");
        
        // Always reset to collapsed state (remove "show" class)
        contents.classList.remove("show");
        
        // Add 'see-more-media' class to divs after the 6th index
        visibleDivs.forEach((div, index) => {
          if (index >= 6) {
            div.classList.add("see-more-media");
          } else {
            div.classList.remove("see-more-media");
          }
        });
      } else {
        // Hide the button if we have fewer than 7 visible items (excluding card-only)
        viewMoreBtn.style.display = "none";
        contents.classList.remove("more-contents");
        contents.classList.remove("show");
        
        // Remove 'see-more-media' class from all divs
        visibleDivs.forEach(div => {
          div.classList.remove("see-more-media");
        });
      }
    };

    // Function to hide card-only images immediately
    const hideCardOnlyImages = () => {
      const allMediaItems = carousel.querySelectorAll(".product-gallery__media");
      allMediaItems.forEach(div => {
        const img = div.querySelector('img');
        if (img && img.getAttribute('alt') === 'card-only') {
          div.style.display = 'none';
        }
      });
    };

    // Hide card-only images immediately
    hideCardOnlyImages();
    
    // Initial update
    updateVisibleMediaItems();

    // Set up click handler for the view more button
    viewMoreBtn.addEventListener("click", () => {
      const contents = document.querySelector(".product .product-gallery .product-gallery--carousel");
      if (contents) {
        contents.classList.toggle("show");
      }
    });

    // Observe changes to the hidden attribute on gallery media items
    const observer = new MutationObserver((mutationsList) => {
      let shouldUpdate = false;
      
      for (const mutation of mutationsList) {
        if (mutation.type === "attributes" && 
            (mutation.attributeName === "hidden" || mutation.attributeName === "alt")) {
          shouldUpdate = true;
          break;
        }
        if (mutation.type === "childList") {
          shouldUpdate = true;
          break;
        }
      }
      
      if (shouldUpdate) {
        // Hide card-only images first
        setTimeout(() => {
          hideCardOnlyImages();
          updateVisibleMediaItems();
        }, 50);
      }
    });

    // Observe all product gallery media items
    const mediaItems = carousel.querySelectorAll(".product-gallery__media");
    mediaItems.forEach(item => {
      observer.observe(item, { 
        attributes: true, 
        attributeFilter: ["hidden", "alt"],
        childList: true,
        subtree: true
      });
    });
    
    // Also observe the carousel for added/removed nodes (variant changes)
    observer.observe(carousel, { 
      childList: true,
      subtree: true
    });
    
    // Run when any variant selector changes
    const variantSelectors = document.querySelectorAll('.product-info .variant-picker__radio,.product-info select[name="id"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        // Reset gallery to collapsed state and update visibility
        const contents = document.querySelector(".product .product-gallery .product-gallery--carousel");
        if (contents) {
          contents.classList.remove("show");
        }
        
        // Wait for Shopify to update the gallery, then hide card-only and update
        setTimeout(() => {
          hideCardOnlyImages();
          updateVisibleMediaItems();
        }, 100);
      });
    });

    // Also listen for product form updates (alternative variant change detection)
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (productForm) {
      productForm.addEventListener('change', () => {
        setTimeout(() => {
          hideCardOnlyImages();
          updateVisibleMediaItems();
        }, 100);
      });
    }
  }

  // Initial setup
  setupProductGallery();
  
  // Rerun setup after a slight delay to ensure everything is loaded
  setTimeout(setupProductGallery, 500);
});

// Add a single global click handler
document.addEventListener("click", (event) => {
  const viewMoreBtn = event.target.closest(".view-more-btn");
  if (viewMoreBtn) {
    const contents = document.querySelector(".product-gallery .product-gallery--carousel");
    if (contents) {
      contents.classList.toggle("show");
    }
  }
});